import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { classApi } from '../../../api/classes';
import { toast } from 'react-toastify';
import { formatCurrencyFromMinor } from '../../../utils/currency';
import ClosedSchoolNotice from './ClosedSchoolNotice';
import './PublicSchool.css';
import './ShortCoursePage.css';

/**
 * ShortCoursePage - Public short course / workshop landing page
 * URL: /school/:orgSlug/course/:courseSlug
 */
const ShortCoursePage = () => {
    const { orgSlug, courseSlug } = useParams();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [showEnquiry, setShowEnquiry] = useState(false);
    const [enquiryName, setEnquiryName] = useState('');
    const [enquiryEmail, setEnquiryEmail] = useState('');
    const [enquiryNotes, setEnquiryNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const r = await classApi.getPublicCoursePage(orgSlug, courseSlug);
                if (r.ok) {
                    setData(r.data);
                } else {
                    setNotFound(true);
                }
            } catch {
                setNotFound(true);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [orgSlug, courseSlug]);

    const handleSubmitEnquiry = async () => {
        if (!enquiryName || !enquiryEmail) return;
        setIsSubmitting(true);
        try {
            const r = await classApi.submitEnquiry(orgSlug, {
                firstName: enquiryName.split(' ')[0],
                lastName: enquiryName.split(' ').slice(1).join(' '),
                email: enquiryEmail,
                experienceNotes: enquiryNotes,
                courseSlug,
            });
            if (r.ok) {
                setSubmitted(true);
            } else {
                // Includes the school having switched class management off (ORG_DOMAIN_CLOSED).
                toast.error(r.data?.message || 'Could not send your enquiry. Please try again.');
            }
        } catch {
            toast.error('Could not send your enquiry. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <div className="public-school"><p className="public-school-loading">Loading...</p></div>;
    }

    if (notFound || !data) {
        return (
            <div className="public-school">
                <div className="public-school-not-found">
                    <h2>Course Not Found</h2>
                    <p>This course page does not exist or has been removed.</p>
                </div>
            </div>
        );
    }

    // Class management switched off by the school: header + closed notice, no enquiry form.
    if (data.isOpen === false) {
        return <ClosedSchoolNotice orgSlug={orgSlug} orgName={data.orgName} branding={data.branding} />;
    }

    const { course, school } = data;
    const b = school?.branding || {};
    const headerColor = b.headerColor || '#451f78';
    const headerTextColor = b.headerTextColor || '#ffffff';
    const backgroundColor = b.backgroundColor || '#f5f0fb';
    const textColor = b.textColor || '#333333';
    const buttonColor = b.buttonColor || '#451f78';
    const buttonTextColor = b.buttonTextColor || '#ffffff';

    const formatDate = (iso) => {
        if (!iso) return '';
        return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    return (
        <div className="public-school" style={{ background: backgroundColor, color: textColor }}>
            <div className="public-school-header public-school-header--detail" style={{ background: headerColor }}>
                <Link to={`/school/${orgSlug}`} className="public-group-back-link" style={{ color: headerTextColor, opacity: 0.75 }}>
                    {school?.name || 'School'}
                </Link>
                <h1 className="public-school-title" style={{ color: headerTextColor }}>{course.name}</h1>
                {course.description && (
                    <p className="public-school-subtitle" style={{ color: headerTextColor }}>{course.description}</p>
                )}
            </div>

            <div className="public-school-body">
                <div className="short-course-details-card" style={{ borderColor: headerColor + '33' }}>
                    {course.startDate && (
                        <div className="short-course-detail-row">
                            <span className="short-course-detail-label">Start Date</span>
                            <span className="short-course-detail-value">{formatDate(course.startDate)}</span>
                        </div>
                    )}
                    {course.endDate && (
                        <div className="short-course-detail-row">
                            <span className="short-course-detail-label">End Date</span>
                            <span className="short-course-detail-value">{formatDate(course.endDate)}</span>
                        </div>
                    )}
                    {course.weekCount && (
                        <div className="short-course-detail-row">
                            <span className="short-course-detail-label">Duration</span>
                            <span className="short-course-detail-value">{course.weekCount} weeks</span>
                        </div>
                    )}
                    {course.style && (
                        <div className="short-course-detail-row">
                            <span className="short-course-detail-label">Style</span>
                            <span className="short-course-detail-value">{course.style}</span>
                        </div>
                    )}
                    {course.price > 0 && (
                        <div className="short-course-detail-row">
                            <span className="short-course-detail-label">Price</span>
                            <span className="short-course-detail-value">{formatCurrencyFromMinor(course.price)}</span>
                        </div>
                    )}
                </div>

                {submitted ? (
                    <div className="short-course-submitted" style={{ borderColor: headerColor + '33' }}>
                        <p className="short-course-submitted-title" style={{ color: headerColor }}>Enquiry Received</p>
                        <p className="short-course-submitted-body">Thanks! We will be in touch shortly.</p>
                    </div>
                ) : showEnquiry ? (
                    <div className="short-course-enquiry-form" style={{ borderColor: headerColor + '33' }}>
                        <p className="short-course-form-title" style={{ color: headerColor }}>Register Interest</p>
                        <input
                            className="short-course-input"
                            placeholder="Full name *"
                            value={enquiryName}
                            onChange={e => setEnquiryName(e.target.value)}
                        />
                        <input
                            className="short-course-input"
                            type="email"
                            placeholder="Email address *"
                            value={enquiryEmail}
                            onChange={e => setEnquiryEmail(e.target.value)}
                        />
                        <textarea
                            className="short-course-input short-course-textarea"
                            placeholder="Any questions or experience notes (optional)"
                            value={enquiryNotes}
                            onChange={e => setEnquiryNotes(e.target.value)}
                            rows={3}
                        />
                        <div className="short-course-form-actions">
                            <button className="short-course-cancel-btn" onClick={() => setShowEnquiry(false)}>Cancel</button>
                            <button
                                className="short-course-submit-btn"
                                style={{ background: buttonColor, color: buttonTextColor }}
                                onClick={handleSubmitEnquiry}
                                disabled={isSubmitting || !enquiryName || !enquiryEmail}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <button
                        className="public-school-enquire-btn"
                        style={{ background: buttonColor, color: buttonTextColor }}
                        onClick={() => setShowEnquiry(true)}
                    >
                        Register Interest
                    </button>
                )}
            </div>
            <p className="public-school-powered-by">Powered by Dance Localé</p>
        </div>
    );
};

export default ShortCoursePage;
